import { BaseRepository } from './BaseRepository'
import { StorageAdapter } from '../storage/StorageAdapter'
import { rowsOf } from '../storage/rows'
import { parseMaybeJson } from '../storage/json-codec'
import { newId } from './ids'

/**
 * OccurrencesRepository — re-implementacion limpia (SQL) de los ops de occurrence de
 * v1 src/offline/modules/occurrences.js (REFERENCIA — NO importado).
 *
 * Bodies NORMALIZADOS (F3 construye el REST). Tabla `occurrences`, entity `occurrence`,
 * pk `client_uuid`.
 *   - saveDraft: latest-state (coalesce) op occurrence.syncOccurrence.
 *   - markClosing: EVENT (no coalesce) op occurrence.close.
 *   - enqueueNAGroup: EVENT (no coalesce, sin fila de datos) op occurrence.naGroup.
 *   - saveComments: latest-state (coalesce) op occurrence.saveComments (F-97).
 * Las firmas viven en SignaturesRepository (occurrence.saveSignature). La marca
 * occ-closed:<occId> la escribe F3 al exito — NO aqui.
 */
export class OccurrencesRepository extends BaseRepository {
  constructor () {
    super('occurrences', 'occurrence', 'client_uuid')
  }

  /**
   * Guarda/actualiza el borrador de una occurrence. Latest-state -> coalesce la fila
   * pending de sync. body minimo: { occurrence_id } (F3 lee los records de la BD al drenar).
   * @param {object} occRow  fila de datos de la occurrence (incluye client_uuid).
   * @returns {Promise<string>} client_uuid.
   */
  async saveDraft (occRow = {}) {
    const uuid = occRow.client_uuid
    return this.saveOptimistic({
      clientUuid: uuid,
      dataRow: { ...occRow, status: 'draft' },
      op: 'occurrence.syncOccurrence',
      coalesce: true,
      body: { occurrence_id: uuid }
    })
  }

  /**
   * Marca la occurrence como en-cierre y encola el EVENT de cierre (no coalesce).
   * El `payload` (si se pasa) es la UNICA frontera suave: puede llevar refs ya
   * pre-construidas, NUNCA binarios (las fotos van por image_ref/local_path).
   * @param {string} clientUuid
   * @param {object} [opts] { payload, tasksEditing }
   * @returns {Promise<string>} client_uuid.
   */
  async markClosing (clientUuid, { payload, tasksEditing = [] } = {}) {
    return this.saveOptimistic({
      clientUuid,
      // `score` también se persiste en la fila local (columna occurrences.score, ver schema.js)
      // además de viajar dentro de body.payload. Antes SOLO vivía en body.payload (JSON del outbox,
      // leído solo al drenar contra el server) -> mientras el close seguía pending/in_flight no había
      // forma de leer el score recién calculado desde la fila local, y el overlay de reingreso
      // (overlayPendingLocalClose) no podía restaurarlo -> currentScore mostraba undefined al reabrir
      // una occurrence cerrada offline-first aún no drenada.
      dataRow: { client_uuid: clientUuid, status: 'closing', score: payload?.score },
      op: 'occurrence.close',
      coalesce: false,
      body: { occurrence_id: clientUuid, payload, tasksEditing }
    })
  }

  /**
   * Guarda los comentarios adicionales de la occurrence (F-97). Son de la occurrence, no de un ítem, así
   * que viven en la columna `occurrences.comments` (v11) de la fila local de la occurrence
   * (client_uuid = id de la occurrence, la misma que usa markClosing). Latest-state -> coalesce: varias
   * ediciones sin red quedan en UNA fila pending con el último texto. Solo escribe client_uuid y
   * comments, así que el UPSERT no pisa status/score de un cierre ya encolado.
   * body: { occurrence_id, comments } (el handler hace el PUT comments/<id> con ese texto).
   * @param {string|number} occurrenceId
   * @param {string} comments
   * @returns {Promise<string>} client_uuid (id de la occurrence).
   */
  async saveComments (occurrenceId, comments, { db = StorageAdapter } = {}) {
    const uuid = String(occurrenceId)
    const text = comments ?? ''
    return this.saveOptimistic({
      clientUuid: uuid,
      dataRow: { client_uuid: uuid, comments: text },
      op: 'occurrence.saveComments',
      coalesce: true,
      body: { occurrence_id: uuid, comments: text }
    }, { db })
  }

  /**
   * Encola el EVENT de NA de un grupo (no coalesce, sin fila de datos): al drenar, el handler llama a
   * `na-group`, el mismo endpoint del camino en línea, que deja en NA cada respuesta del grupo.
   *
   * En la MISMA transacción, las occurrence.syncOccurrence pendientes de la occurrence se mueven DETRÁS
   * de esta op (se borran y se reinsertan con su client_uuid, retries y user_id). Una respuesta capturada
   * después del NA coalesce en su op pendiente: si esa op quedara antes del na-group, la respuesta subiría
   * primero y el na-group la borraría. Las in_flight no se mueven: ya están drenando antes que esta op.
   * @param {string} occurrenceId
   * @param {object} opts { groupId, itemIds }
   * @param {object} [deps] { db }
   * @returns {Promise<string>} client_uuid de la op.
   */
  enqueueNAGroup (occurrenceId, { groupId, itemIds = [] } = {}, { db = StorageAdapter } = {}) {
    return db.transaction(handle => this.enqueueNAGroupIn(handle, occurrenceId, { groupId, itemIds }))
  }

  /** Igual que enqueueNAGroup, dentro de una transacción ya abierta (`handle` es su handle). */
  async enqueueNAGroupIn (handle, occurrenceId, { groupId, itemIds = [] } = {}) {
    const uuid = `nag:${occurrenceId}:${groupId}:${newId()}`
    const naGroupInsert = this._buildOutboxInsert({
      clientUuid: uuid,
      entity: this.entity,
      op: 'occurrence.naGroup',
      body: { occurrence_id: occurrenceId, group_id: groupId, item_ids: itemIds }
    })
    const pendingSyncOps = rowsOf(await handle.query(
      "SELECT seq, client_uuid, entity, op, body, retries, user_id FROM outbox WHERE op='occurrence.syncOccurrence' AND status='pending'"
    )).filter(row => String(parseMaybeJson(row.body)?.occurrence_id) === String(occurrenceId))

    for (const row of pendingSyncOps) {
      await handle.run("DELETE FROM outbox WHERE seq=? AND status='pending'", [row.seq], false)
    }
    await handle.run(naGroupInsert.statement, naGroupInsert.values, false)
    for (const row of pendingSyncOps) {
      const now = Date.now()
      await handle.run(
        'INSERT INTO outbox (mutation_uuid, client_uuid, entity, op, body, status, retries, next_attempt_at, last_error, created_at, user_id) ' +
        'VALUES (?,?,?,?,?,?,?,?,?,?,?)',
        [newId(), row.client_uuid, row.entity, row.op, row.body, 'pending', row.retries, now, null, now, row.user_id],
        false
      )
    }
    return uuid
  }
}

export const occurrencesRepository = new OccurrencesRepository()
